import { useTranslation } from "@msuite/ui/localization";
import { useQueryClient } from "@msuite/ui/query";
import { DataTable } from "@msuite/ui/table";
import { toast } from "@msuite/ui/toast";
import { responseHandler } from "@msuite/ui/utils";
import { useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { client } from "@/hono";
import { useFormAutosave } from "@/hooks/use-form-autosave";
import {
	type NotificationPreferencesData,
	useNotificationPreferences,
} from "@/hooks/use-notification-preferences";
import {
	type NotificationPreferencesFormValues,
	toNotificationPreferencesFormValues,
	toNotificationPreferencesPatch,
} from "./notification-preference-form-values";
import { createColumns } from "./table-config";

/**
 * The signed-in person's choice of which notifications reach them, one row per catalogue kind
 * grouped as the catalogue groups them, with a push and an e-mail checkbox each. Every checkbox saves
 * on its own, the way the permission grid saves on selection.
 */
export function NotificationPreferences() {
	/** Context */
	const { t, language } = useTranslation();

	/** Hooks */
	const qc = useQueryClient();

	/** Query */
	const { preferences, hasEmail, isLoading, queryKey } = useNotificationPreferences();

	/** Form */
	const values = useMemo(
		() => (preferences ? toNotificationPreferencesFormValues(preferences) : undefined),
		[preferences],
	);
	const methods = useForm<NotificationPreferencesFormValues>({
		defaultValues: { preferences: {} },
		values,
		resetOptions: { keepDirtyValues: true },
	});

	/** Functions */
	async function handleOnSubmit(json: NotificationPreferencesFormValues) {
		// The baseline is the cache, read at send time: a save queued behind another compares
		// against what the previous one stored, not against the render it was scheduled from.
		const saved = qc.getQueryData<NotificationPreferencesData>(queryKey);
		if (!saved) return;
		const patch = toNotificationPreferencesPatch(
			json,
			toNotificationPreferencesFormValues(saved.preferences),
		);
		if (Object.keys(patch.preferences).length === 0) return;
		try {
			const response = await client.users["notification-preferences"].$patch({ json: patch });
			const data = await responseHandler(response, false);
			const stored = { preferences: data.preferences, hasEmail: data.hasEmail };
			qc.setQueryData(queryKey, stored);
			// The burst's last save settles the form on what the server stored.
			if (!autosave.hasQueuedSave()) {
				methods.reset(toNotificationPreferencesFormValues(stored.preferences));
			}
		} catch (error) {
			// A checkbox showing a choice the server refused would be a lie; the next one is the retry.
			toast.error(error instanceof Error ? error.message : t("general.error"));
			methods.reset(toNotificationPreferencesFormValues(saved.preferences));
		}
	}

	const onSubmit = methods.handleSubmit(handleOnSubmit);
	const autosave = useFormAutosave(methods, onSubmit);

	/** Computed */
	const columns = useMemo(() => createColumns({ hasEmail }), [hasEmail, language]);
	const data = useMemo(() => preferences?.map((row) => ({ ...row, id: row.kind })), [preferences]);

	/** Render */
	return (
		<FormProvider {...methods}>
			<DataTable
				persistenceKey="notification-preferences-table"
				data={data}
				columns={columns}
				isLoading={isLoading}
				queryKey={undefined}
				// No refresh control: the values live in the form, and a refetch mid-edit would
				// discard an edit whose save is still in flight.
				refetch={undefined}
				menu={null}
				enableSorting={false}
				enableSearch={false}
				grouping={["group"]}
				initialColumnVisibility={{ group: false }}
				initialPinningState={{ start: ["kind"] }}
				renderGroupHeader={(row) => <p>{t(`notifications.groups.${row.original.group}`)}</p>}
				slots={{
					"above-search": () => (
						<div className="v-stack gap-1">
							<p className="label">{t("notifications.preferences.description")}</p>
							{!hasEmail && <p className="label">{t("notifications.preferences.no_email_hint")}</p>}
						</div>
					),
				}}
			/>
		</FormProvider>
	);
}
